class ProductSlider {
    constructor(container) {
        this.container = container;
        this.slider = container.querySelector('[data-products-slider]');
        this.progressBar = container.querySelector('[data-progress-bar]');
        this.prevBtn = container.querySelector('[data-prev-btn]');
        this.nextBtn = container.querySelector('[data-next-btn]');
        this.mobileNav = container.querySelector('[data-mobile-nav]');
        
        if (!this.slider) return;
        
        this.currentIndex = 0;
        this.products = Array.from(this.slider.children);
        this.totalProducts = this.products.length;
        
        // Track drag state for click detection
        this.isDragging = false;
        
        this.init();
    }

    init() {
        this.calculateDimensions();
        this.bindEvents();
        this.preventClickOnDrag();
        
        requestAnimationFrame(() => {
            this.updateProgressBar();
            this.updateButtons();
            this.createMobileDots();
        });
    }

    calculateDimensions() {
        this.isMobile = window.innerWidth < 768;
        this.itemsPerView = this.isMobile ? 2 : 4;
        
        if (this.products.length > 0) {
            const firstProduct = this.products[0];
            this.itemWidth = firstProduct.offsetWidth;
            
            const productsStyle = window.getComputedStyle(this.slider);
            this.gap = parseInt(productsStyle.gap) || (this.isMobile ? 16 : 38);
        }
    }

    bindEvents() {
        if (this.nextBtn) {
            this.nextBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.next();
            });
        }
        if (this.prevBtn) {
            this.prevBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.prev();
            });
        }
        
        this.addTouchSupport();
        
        window.addEventListener('resize', () => this.handleResize());
    }

    preventClickOnDrag() {
        const productLinks = this.container.querySelectorAll('[data-product-link]');
        
        productLinks.forEach(link => {
            link.addEventListener('click', (e) => {
                if (this.isDragging) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            });
        });
    }

    addTouchSupport() {
        const touchTarget = this.slider.parentElement || this.slider;
        
        if (!touchTarget) return;

        let startX = null;
        let startY = null;
        let currentX = null;
        let currentY = null;
        let isPointerDown = false;
        let isHorizontalSwipe = false;
        let hasTouchSupport = 'ontouchstart' in window;

        const resetState = () => {
            startX = null;
            startY = null;
            currentX = null;
            currentY = null;
            isPointerDown = false;
            isHorizontalSwipe = false;
            
            // Reset isDragging after a short delay to allow click event to fire
            setTimeout(() => {
                this.isDragging = false;
            }, 10);
        };

        const handleStart = (clientX, clientY) => {
            startX = clientX;
            startY = clientY;
            currentX = clientX;
            currentY = clientY;
            isPointerDown = true;
            isHorizontalSwipe = false;
            this.isDragging = false;
        };

        const handleMove = (clientX, clientY) => {
            if (!isPointerDown || startX === null || startY === null) {
                return;
            }

            currentX = clientX;
            currentY = clientY;

            const diffX = currentX - startX;
            const diffY = currentY - startY;

            // Check if user has moved enough to be considered dragging
            if (Math.abs(diffX) > 5 || Math.abs(diffY) > 5) {
                this.isDragging = true;
            }

            if (!isHorizontalSwipe && (Math.abs(diffX) > 15 || Math.abs(diffY) > 15)) {
                isHorizontalSwipe = Math.abs(diffX) > Math.abs(diffY);
            }
        };

        const handleEnd = () => {
            if (!isPointerDown || startX === null || currentX === null) {
                resetState();
                return;
            }

            const diffX = currentX - startX;

            if (isHorizontalSwipe && Math.abs(diffX) > 50) {
                if (diffX > 0) {
                    this.prev();
                } else {
                    this.next();
                }
            }

            resetState();
        };

        touchTarget.addEventListener('touchstart', (e) => {
            const touch = e.touches[0];
            handleStart(touch.clientX, touch.clientY);
        }, { passive: true });

        touchTarget.addEventListener('touchmove', (e) => {
            if (!isPointerDown) return;
            const touch = e.touches[0];
            handleMove(touch.clientX, touch.clientY);
            
            if (isHorizontalSwipe) {
                e.preventDefault();
            }
        }, { passive: false });

        touchTarget.addEventListener('touchend', () => {
            handleEnd();
        }, { passive: true });

        touchTarget.addEventListener('touchcancel', () => {
            resetState();
        }, { passive: true });

        if (!hasTouchSupport) {
            touchTarget.addEventListener('mousedown', (e) => {
                handleStart(e.clientX, e.clientY);
                e.preventDefault();
            });

            touchTarget.addEventListener('mousemove', (e) => {
                if (!isPointerDown) return;
                handleMove(e.clientX, e.clientY);
            });

            touchTarget.addEventListener('mouseup', () => {
                if (!isPointerDown) return;
                handleEnd();
            });

            touchTarget.addEventListener('mouseleave', () => {
                if (isPointerDown) {
                    handleEnd();
                }
            });
        }
    }

    handleResize() {
        const wasMobile = this.isMobile;
        this.calculateDimensions();
        
        if (wasMobile !== this.isMobile) {
            this.currentIndex = 0;
            this.updateSlider();
        }
        
        this.updateProgressBar();
        this.updateButtons();
        this.createMobileDots();
    }

    next() {
        const maxIndex = Math.max(0, this.totalProducts - this.itemsPerView);
        const remainingItems = this.totalProducts - this.currentIndex - this.itemsPerView;
        
        if (remainingItems > 0) {
            const itemsToMove = Math.min(this.itemsPerView, remainingItems);
            this.currentIndex = Math.min(maxIndex, this.currentIndex + itemsToMove);
            this.updateSlider();
            this.updateProgressBar();
            this.updateButtons();
            this.updateMobileDots();
        }
    }

    prev() {
        if (this.currentIndex > 0) {
            const itemsToMove = Math.min(this.itemsPerView, this.currentIndex);
            this.currentIndex = Math.max(0, this.currentIndex - itemsToMove);
            this.updateSlider();
            this.updateProgressBar();
            this.updateButtons();
            this.updateMobileDots();
        }
    }

    updateSlider() {
        if (!this.itemWidth) {
            return;
        }
        
        const translateX = -(this.currentIndex * (this.itemWidth + this.gap));
        this.slider.style.transform = `translateX(${translateX}px)`;
    }

    updateProgressBar() {
        if (!this.progressBar) return;
        
        const lastVisibleItemIndex = this.currentIndex + this.itemsPerView - 1;
        const itemsSeenFromStart = Math.min(lastVisibleItemIndex + 1, this.totalProducts);
        const progressPercentage = (itemsSeenFromStart / this.totalProducts) * 100;
        
        this.progressBar.style.width = `${progressPercentage}%`;
    }

    updateButtons() {
        if (this.prevBtn) {
            this.prevBtn.disabled = this.currentIndex === 0;
        }
        if (this.nextBtn) {
            this.nextBtn.disabled = this.currentIndex >= this.totalProducts - this.itemsPerView;
        }
    }

    createMobileDots() {
        if (!this.mobileNav || !this.isMobile) {
            if (this.mobileNav) this.mobileNav.innerHTML = '';
            return;
        }

        const totalPages = Math.ceil(this.totalProducts / this.itemsPerView);
        const currentPage = Math.floor(this.currentIndex / this.itemsPerView);
        
        this.mobileNav.innerHTML = '';
        
        for (let i = 0; i < totalPages; i++) {
            const dot = document.createElement('button');
            if (i === currentPage) {
                dot.classList.add('active');
            }
            dot.addEventListener('click', () => {
                this.currentIndex = i * this.itemsPerView;
                this.updateSlider();
                this.updateProgressBar();
                this.updateButtons();
                this.updateMobileDots();
            });
            this.mobileNav.appendChild(dot);
        }
    }

    updateMobileDots() {
        if (!this.isMobile || !this.mobileNav) return;

        const currentPage = Math.floor(this.currentIndex / this.itemsPerView);
        const dots = this.mobileNav.children;
        
        Array.from(dots).forEach((dot, index) => {
            dot.classList.toggle('active', index === currentPage);
        });
    }
}

function initProductSliders() {
    const sliders = document.querySelectorAll('[data-slider]');
    sliders.forEach(slider => new ProductSlider(slider));
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductSliders);
} else {
    initProductSliders();
}